import axios, { AxiosInstance } from 'axios';
import { acquireTokenSilent, isAuthEnabled, clearMsalAccounts } from '@/auth';
import { config } from '@/config';
import { useAuthStore } from '@/stores/useAuthStore';
import { useNotificationStore } from '@/stores/useNotificationStore';

const API_BASE_URL = config.apiUrl;

const isRecordLevelAccessEndpoint = (requestUrl: string): boolean => {
  if (!requestUrl) {
    return false;
  }

  // Normalize absolute and relative URLs to pathname only
  const normalizedPath = requestUrl.startsWith('http')
    ? new URL(requestUrl).pathname
    : requestUrl;

  if (normalizedPath.includes('/my-access')) {
    return true;
  }

  // Record-level pages can legitimately return 401/403 when access is revoked
  // (e.g., delegation revoked while page is still mounted).
  const jobEndScopedPattern = /(?:\/api)?\/job-ends\/\d+(?:\/.*)?$/;
  return jobEndScopedPattern.test(normalizedPath);
};

/**
 * Create an axios instance with authentication interceptor
 * and 401 response handling for cross-tab logout scenarios
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add authentication interceptor if Azure AD is configured
  client.interceptors.request.use(async (config) => {
    if (isAuthEnabled()) {
      const token = await acquireTokenSilent();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add impersonation header if impersonating
    const impersonatedUser = useAuthStore.getState().impersonatedUser;
    if (impersonatedUser?.staffId) {
      config.headers['X-Impersonate-User'] = impersonatedUser.staffId.toString();
    }

    return config;
  });

  // Add response interceptor to handle 401 and 403 errors
  // 401: Authentication failure - redirect to login
  // 403: Authorization failure - redirect to error page (except /me/permissions)
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const currentPath = window.location.pathname;

      // Priority 1: 401 Unauthorized
      if (status === 401 && isAuthEnabled()) {
        const requestUrl = error.config?.url || error.response?.config?.url || '';

        // Skip redirect for record-level access checks.
        // 401 here means "lost access to this record", not "session expired".
        if (isRecordLevelAccessEndpoint(requestUrl)) {
          return Promise.reject(error);
        }

        // Clear all auth state and redirect to login
        clearMsalAccounts();
        useAuthStore.getState().clearPermissions();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Priority 2: 403 Forbidden
      if (status === 403) {
        const requestUrl = error.config?.url || error.response?.config?.url || '';

        console.error('[API 403 Forbidden]', {
          url: requestUrl,
          method: error.config?.method,
          message: error.response?.data?.message,
          error,
        });

        // Skip redirect for /me/permissions - ProtectedRoute handles this (Account Not Registered)
        // Skip redirect for record-level endpoints - handled by page components.
        if (requestUrl.includes('/me/permissions') || isRecordLevelAccessEndpoint(requestUrl)) {
          return Promise.reject(error);
        }

        // Skip redirect for endpoints that handle 403 locally — both Job End and Appraisal scopes:
        // - /senior-feedback, /eligible-senior-reviewers, /audit-history: Job End features that may be
        //   unavailable for a given JE (e.g. no senior review required, no audit access).
        // - /ai-export, /ai-import: Appraisal AI endpoints gated by canExportToAi / canImportFromAi;
        //   the caller surfaces a snackbar/dialog error instead of navigating away.
        // - /upward-feedback/embed: the Upward Feedback section renders its own alert; a 403 here
        //   means the section is not visible to this caller, not that the page is inaccessible.
        // Components using these endpoints hide themselves or show a local error rather than crashing.
        const endpointsHandling403Locally = ['/senior-feedback', '/eligible-senior-reviewers', '/audit-history', '/ai-export', '/ai-import', '/upward-feedback/embed'];
        if (endpointsHandling403Locally.some(ep => requestUrl.includes(ep))) {
          return Promise.reject(error);
        }

        // Loop prevention: skip redirect if already on error page
        if (currentPath === '/error' || currentPath === '/unauthorized' || currentPath === '/not-registered') {
          console.error('Already on error page, skipping 403 redirect to prevent loop');
          return Promise.reject(error);
        }

        window.location.href = '/error';
        return Promise.reject(error);
      }

      // Priority 3: 409 Conflict (concurrency conflict)
      if (status === 409) {
        const message = error.response?.data?.message
          || 'This record was modified by another user. Your changes were not saved. The page has been refreshed with the latest data.';
        useNotificationStore.getState().showNotification(message, 'warning');
        return Promise.reject(error);
      }

      // Note: 404 errors are NOT redirected - they should be handled by individual API methods
      // as 404 can be a valid "not found" response (e.g., no active appraisal cycle)

      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createApiClient();
